import { Link, useNavigate, useParams } from "react-router-dom";
import { useToasts } from "../../contexts/toasts";
import { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Breadcrumb, BreadcrumbItem, Button, Card, Col, Container, Modal, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import AppLoader from "../../components/common/AppLoader";
import { Helmet } from "react-helmet";
import { Form, Input, Tooltip } from "antd";
import { useFullScreenLoader } from "../../contexts/fullScreenLoader";
import { DataRetriever } from "../../api/DataRetriever";
import { DataspaceConnector } from "../../models/DataSpace";
import DataSpaceConnectorForm from "../../components/forms/DataSpaceConnectorForm";
import { useAuth } from "../../contexts/auth";

const formLayout = {
  labelCol: {
    xs: 24,
    md: 6,
  },
  wrapperCol: {
    xs: 24,
    md: 18,
  },
}

export default function DataspaceConnectorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loggedUser } = useAuth();

  const { showToast } = useToasts();
  const [connector, setConnector] = useState<DataspaceConnector>();
  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [resource, setResource] = useState<string | undefined>();
  const [form] = Form.useForm();
  const [error, setError] = useState(false);

  const { showLoader, hideLoader } = useFullScreenLoader();

  const getConnector = useCallback(() => {
    DataRetriever.getDataspaceConnector(id!)
      .then(
        ({ data }) => setConnector(data),
        () => {
          showToast('dataspace-connector-error', 'Could not get connector', 'Connector Error', 'danger');
          setError(true);
        }
      );
  }, [id, showToast]);

  useEffect(() => {
    if (id) getConnector();
  }, [id, getConnector]);

  const reset = useCallback(() => {
    setConnector(undefined);
    getConnector();
  }, [getConnector]);

  const handleClose = useCallback(() => {
    setShowUnpublishModal(false);
    setShowDeleteModal(false);
    setShowEditModal(false);
    form.resetFields();
  }, [form]);

  const openUpublishModal = useCallback((resourceId: string) => () => {
    setResource(resourceId);
    setShowUnpublishModal(true);
  }, []);

  const openDeleteModal = useCallback(() => setShowDeleteModal(true), []);
  const openEditModal = useCallback(() => setShowEditModal(true), []);

  const handleUnpublish = useCallback((values: any) => {
    showLoader();

    DataRetriever.unpublishResource(connector!.id, resource!, values.user, values.password)
      .then(
        () => {
          hideLoader();
          reset();
          setShowUnpublishModal(false);
          showToast('unpublish-success', 'Resource unpublished successfully', 'Resource Success', 'success');
        },
        () => {
          hideLoader();
          showToast('unpublish-error', 'Could not unpublish resource', 'Resource Error', 'danger');
        }
      )
  }, [connector, resource, reset, showToast, hideLoader, showLoader]);

  const handleSubmit = useCallback((values: DataspaceConnector) => {
    showLoader();

    DataRetriever.editDataspaceConnector(values!)
      .then(
        () => {
          hideLoader();
          handleClose();
          showToast('connector-success', 'Connector updated successfully', 'Connector Updated', 'success');
          reset();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('connector-error', message, `Connectors Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [reset, handleClose, showLoader, hideLoader, showToast]);

  const deleteConnector = useCallback(() => {
    showLoader();

    DataRetriever.deleteDataspaceConnnector(connector!.id)
      .then(
        () => {
          hideLoader();
          handleClose();
          showToast('connector-success', 'Data Space connector deleted successfully', 'Connector Success', 'success');
          navigate('/dataspace-connectors');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('connector-error', message, `Connector Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showLoader, hideLoader, handleClose, navigate, connector, showToast]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occured while retrieving connector. Try again later.</span>
      </Alert>
    );

    if (!connector) return (
      <div className="d-flex justify-content-center py-5">
        <AppLoader size={300} />
      </div>
    );

    return (
      <>
        <Breadcrumb>
          <BreadcrumbItem linkAs={Link} linkProps={{ to: '/dataspace-connectors' }}>Connectors</BreadcrumbItem>
          <Breadcrumb.Item active>{connector?.name}</Breadcrumb.Item>
        </Breadcrumb>

        <Card className="item-detail-group main bg-datasource">
          <FontAwesomeIcon className="item-icon" icon={solid('plug')} />

          <div className="p-2 py-4">
            <Row className="gx-0 align-items-center">
              <Col className="pe-3">
                <h3 className="mb-0">{connector.name}</h3>
                <p className="text-trailing mb-2">{connector.description}</p>
                <Tooltip title="Connector URL">
                  <Link to={connector.connectorUrl} target="_blank" className="text-trailing mb-3">
                    <Badge className="connector-url-badge">{connector.connectorUrl}</Badge>
                  </Link>
                </Tooltip>
              </Col>

              {loggedUser?.sub === connector.ownershipInfo?.ownerId && (
                <Col md={3} lg={2} className="text-end">
                  <button className="circle-button p-4" onClick={openEditModal}>
                    <FontAwesomeIcon icon={solid('pen')} />
                  </button>
                  <button className="circle-button p-4 ms-3" onClick={openDeleteModal}>
                    <FontAwesomeIcon icon={solid('trash')} />
                  </button>
                </Col>
              )}
            </Row>
          </div>
        </Card>

        <div className="d-flex flex-row my-2 align-items-center">
          <FontAwesomeIcon icon={solid('tags')} />
          <h4 className="ms-2 my-auto">Offers</h4>
        </div>

        <Row>
          {connector.resources?.map((resource) => (
            <Col key={resource.id} md={3} className="px-2 mb-3">
              <Card className="shadow-sm resource-card overflow-hidden h-100">
                <Card.Body className="py-2">
                  <Link to={resource.alidaUrl}>
                    <Card.Title className="d-inline lh-base mb-0 mw-100 text-truncate">{resource.name}</Card.Title>
                  </Link>
                  <p className="line-clamp-2 text-secondary mb-4">{resource.description}</p>
                  <Row className="justify-content-center">
                    <Tooltip title="Unpublish">
                      <button className="circle-button dark" onClick={openUpublishModal(resource.id)}>
                        <FontAwesomeIcon icon={solid('xmark-square')} />
                      </button>
                    </Tooltip>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </>
    )
  }, [connector, loggedUser, error, openUpublishModal, openEditModal, openDeleteModal]);

  return (
    <Container className="mt-3">
      <Helmet>
        <title>Connector {id} Detail</title>
      </Helmet>

      {renderAsyncContent()}

      <Modal show={showUnpublishModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Unpublish</Modal.Title>
        </Modal.Header>
        <Form form={form} {...formLayout} onFinish={handleUnpublish}>
          <Modal.Body>
            <Form.Item label="User" name="user" rules={[{ required: true, message: 'User is required.' }]}>
              <Input placeholder="User" />
            </Form.Item>
            <Form.Item label="Password" name="password" rules={[{ required: true, message: 'Password is required.' }]}>
              <Input.Password placeholder="Password" />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <Button type="button" variant="light" size="sm" className="border" onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="success" size="sm">
              <FontAwesomeIcon icon={solid('save')} />
              <span className="ms-2">Save</span>
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showEditModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Data Space Connector</Modal.Title>
        </Modal.Header>
        <DataSpaceConnectorForm connector={connector} onCancel={handleClose} onSubmit={handleSubmit} />
      </Modal>

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Data Space Connector</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete data space connector <b>{connector?.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteConnector}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}